import { Fragment, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { ChevronRight, Filter, PackageCheck, Plus } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import {
  useCreateReception,
  usePurchaseOrder,
  usePurchaseOrders,
  useReceptions,
} from "~/hooks/purchasing";
import { usePagination } from "~/hooks/usePagination";
import { toast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import type { Plant, ReceptionItemFormData } from "~/types/purchasing";
import {
  CURRENCY_SYMBOLS,
  ORDER_TYPE_LABELS,
  PLANT_LABELS,
  UNIT_LABELS,
} from "~/types/purchasing";
import { formatDate } from "~/utils/helpers";

import { ReceptionDialog } from "./components/ReceptionDialog";

// Purchase-order statuses that can still receive stock (mirrors the OC detail page).
const RECEIVABLE_STATUSES = ["draft", "sent", "partial"];

const PAGE_SIZE = 15;

function formatMoney(value: number, currency: string): string {
  const symbol =
    CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || "$";
  return `${symbol}${new Intl.NumberFormat("es-CL").format(value)}`;
}

export function IngresoPage() {
  const navigate = useNavigate();

  const [plant, setPlant] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Register-ingreso flow: pick a pending OC, then open the reception dialog.
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [pickedOrderId, setPickedOrderId] = useState("");
  const [receptionOrderId, setReceptionOrderId] = useState<string | null>(null);

  const { data: receptions = [], isLoading } = useReceptions();
  const { data: allOrders = [] } = usePurchaseOrders();
  const { data: receptionOrder } = usePurchaseOrder(
    receptionOrderId ?? undefined
  );
  const createReception = useCreateReception();

  const pendingOrders = useMemo(
    () => allOrders.filter((o) => RECEIVABLE_STATUSES.includes(o.status)),
    [allOrders]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return receptions.filter((r) => {
      if (plant !== "all" && r.plant !== plant) return false;
      if (dateFrom && r.reception_date < dateFrom) return false;
      if (dateTo && r.reception_date > dateTo) return false;
      if (term) {
        const poNumber = r.purchase_order?.po_number
          ? String(r.purchase_order.po_number)
          : "";
        const supplier = r.purchase_order?.supplier?.name?.toLowerCase() ?? "";
        if (!poNumber.includes(term) && !supplier.includes(term)) return false;
      }
      return true;
    });
  }, [receptions, plant, dateFrom, dateTo, search]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    filtered,
    PAGE_SIZE
  );

  const handleContinueSelection = () => {
    if (!pickedOrderId) return;
    setReceptionOrderId(pickedOrderId);
    setShowSelectDialog(false);
    setPickedOrderId("");
  };

  const handleReceiveItems = async (
    items: ReceptionItemFormData[],
    receptionPlant: Plant,
    observations?: string
  ) => {
    if (!receptionOrder) return;
    try {
      await createReception.mutateAsync({
        purchase_order_id: receptionOrder.id,
        reception_date: dayjs().format("YYYY-MM-DD"),
        plant: receptionPlant,
        observations,
        items,
      });
      toast({
        title: "Ingreso registrado",
        description: `Recepción registrada en ${receptionPlant}. Stock actualizado.`,
      });
      setReceptionOrderId(null);
    } catch (error) {
      toast({
        title: "Error al registrar el ingreso",
        description:
          error instanceof Error ? error.message : "Intenta nuevamente.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          title="Ingreso"
          description="Ingresos (recepciones) registrados sobre las órdenes de compra"
        />
        <TableSkeleton rows={8} columns={8} />
      </PageContainer>
    );
  }

  const registerButton = (
    <Button onClick={() => setShowSelectDialog(true)}>
      <Plus className="mr-2 h-4 w-4" />
      Registrar Ingreso
    </Button>
  );

  return (
    <PageContainer>
      <PageHeader
        title="Ingreso"
        description="Ingresos (recepciones) registrados sobre las órdenes de compra"
        actions={registerButton}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              placeholder="Buscar por OC o proveedor"
              className="h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={plant} onValueChange={setPlant}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todas las plantas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las plantas</SelectItem>
                <SelectItem value="P1">{PLANT_LABELS.P1}</SelectItem>
                <SelectItem value="P2">{PLANT_LABELS.P2}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              className="h-9"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              type="date"
              className="h-9"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <EmptyState
              icon={PackageCheck}
              title="Sin ingresos"
              description="Aún no se han registrado ingresos de compras. Registra uno desde una orden pendiente de recepción."
              action={registerButton}
              className="m-6"
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>OC</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Planta</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Ítems</TableHead>
                    <TableHead>Registrado por</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageItems.map((r, index) => {
                    const isExpanded = expandedId === r.id;
                    const currency = r.purchase_order?.currency ?? "CLP";
                    const itemCount = r.items?.length ?? 0;
                    return (
                      <Fragment key={r.id}>
                        <TableRow
                          className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/30"
                          style={{
                            animationDelay: `${Math.min(index * 40, 400)}ms`,
                          }}
                          onClick={() =>
                            setExpandedId(isExpanded ? null : r.id)
                          }
                        >
                          <TableCell>
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                                isExpanded && "rotate-90"
                              )}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {r.purchase_order?.po_number ? (
                              <button
                                className="text-primary hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/purchasing/orders/${r.purchase_order?.id}`
                                  );
                                }}
                              >
                                #
                                {String(r.purchase_order.po_number).padStart(
                                  5,
                                  "0"
                                )}
                              </button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {r.purchase_order?.supplier?.name ?? "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(r.reception_date)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="whitespace-nowrap"
                            >
                              {PLANT_LABELS[r.plant] ?? r.plant}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {r.purchase_order?.order_type
                              ? ORDER_TYPE_LABELS[r.purchase_order.order_type]
                              : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {itemCount}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {r.received_by_profile?.name ?? "-"}
                          </TableCell>
                        </TableRow>

                        {isExpanded && (
                          <TableRow className="bg-muted/20 duration-200 animate-in fade-in-0 slide-in-from-top-1 hover:bg-muted/20">
                            <TableCell />
                            <TableCell colSpan={7}>
                              <div className="space-y-1.5 py-1">
                                {r.items?.length ? (
                                  r.items.map((it) => {
                                    const unit =
                                      it.purchase_order_item?.unit ?? "";
                                    return (
                                      <div
                                        key={it.id}
                                        className="flex flex-wrap items-center justify-between gap-2 text-sm"
                                      >
                                        <span className="text-muted-foreground">
                                          {it.purchase_order_item?.description ??
                                            "-"}
                                        </span>
                                        <span className="font-mono">
                                          {new Intl.NumberFormat(
                                            "es-CL"
                                          ).format(it.quantity_received)}{" "}
                                          {UNIT_LABELS[unit] ?? unit} ·{" "}
                                          {formatMoney(
                                            it.actual_unit_cost,
                                            currency
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <span className="text-sm text-muted-foreground">
                                    Sin ítems
                                  </span>
                                )}
                                {r.observations && (
                                  <p className="pt-1 text-xs text-muted-foreground">
                                    {r.observations}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                page={page}
                pageCount={pageCount}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                itemLabel="ingresos"
                className="border-t px-4 py-3"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Step 1: pick the purchase order to receive */}
      <Dialog open={showSelectDialog} onOpenChange={setShowSelectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Ingreso</DialogTitle>
            <DialogDescription>
              Selecciona la orden de compra pendiente de recepción.
            </DialogDescription>
          </DialogHeader>
          {pendingOrders.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">
              No hay órdenes de compra pendientes de recepción.
            </p>
          ) : (
            <Select value={pickedOrderId} onValueChange={setPickedOrderId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Seleccionar orden de compra" />
              </SelectTrigger>
              <SelectContent>
                {pendingOrders.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    #{String(o.po_number).padStart(5, "0")} ·{" "}
                    {o.supplier?.name ?? "Sin proveedor"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSelectDialog(false)}
            >
              Cancelar
            </Button>
            <Button onClick={handleContinueSelection} disabled={!pickedOrderId}>
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2: register the reception for the picked order */}
      {receptionOrder && (
        <ReceptionDialog
          open={!!receptionOrderId}
          onOpenChange={(open) => {
            if (!open) setReceptionOrderId(null);
          }}
          items={receptionOrder.items ?? []}
          currency={receptionOrder.currency}
          purchaseOrderId={receptionOrder.id}
          onSubmit={handleReceiveItems}
          isLoading={createReception.isPending}
        />
      )}
    </PageContainer>
  );
}

export default IngresoPage;
